/**
 * Akten & Chats eines Mandanten in der Benutzerverwaltung (Admin → Mandanten).
 * Aktenzeichen / Angelegenheit bearbeiten, Akten archivieren & reaktivieren,
 * Chat sperren/freigeben, Mandantennummer vergeben, Website-Zugang einrichten.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Archive, ArchiveRestore, Lock, Unlock, MessageSquare, Pencil, Check, X, Plus, Mail, AlertCircle, KeyRound } from 'lucide-react';
import { api } from '../context/AuthContext';
import { ZugangVerlauf, AnsprechpersonSelect } from './ZugangVerlauf';
import { ZugaengeCodes } from './ZugaengeCodes';

const RECHTSGEBIETE = ['Ausländerrecht', 'Strafrecht', 'Steuerberatung', 'Steuerrecht', 'Gesellschaftsrecht', 'Verwaltungsrecht', 'Mietrecht', 'Verkehrsrecht', 'Sonstiges'];
const STATUS_LABEL = { neu: 'Neu', in_bearbeitung: 'In Bearbeitung', kontaktiert: 'Kontaktiert', abgeschlossen: 'Abgeschlossen', abgelehnt: 'Abgelehnt' };
const btn = 'inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-wider border rounded-sm transition-colors disabled:opacity-50';

const AkteZeile = ({ akte, onSaved, onError }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ aktenzeichen: akte.aktenzeichen || '', rechtsgebiet: akte.rechtsgebiet || 'Sonstiges', description: akte.description_excerpt || '' });
  const [saving, setSaving] = useState(false);

  const patch = async (payload) => {
    setSaving(true);
    try { const r = await api.patch(`/cases/${akte.id}`, payload); onSaved(r.data); setEdit(false); }
    catch (e) { onError(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 px-4 py-3 border-t border-gray-100 items-start ${akte.archived ? 'bg-gray-50 opacity-80' : 'bg-white'}`} data-testid={`akte-zeile-${akte.id}`}>
      <div className="md:col-span-3">
        {edit ? (
          <input value={form.aktenzeichen} onChange={(e) => setForm((f) => ({ ...f, aktenzeichen: e.target.value }))} placeholder="AZ aus DATEV (000000-26)" data-testid={`akte-az-input-${akte.id}`}
            className="w-full px-2 py-1 text-xs font-mono border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
        ) : (
          <div>
            <span className="font-mono text-sm text-[#1B3A5C]" data-testid={`akte-az-${akte.id}`}>{akte.aktenzeichen ? `AZ ${akte.aktenzeichen}` : <span className="text-amber-600 text-xs">ohne Aktenzeichen</span>}</span>
            <div className="text-[10px] text-[#7a8a99]">{akte.created_at ? new Date(akte.created_at).toLocaleDateString('de-DE') : ''}</div>
          </div>
        )}
      </div>
      <div className="md:col-span-4">
        {edit ? (
          <div className="space-y-1.5">
            <select value={form.rechtsgebiet} onChange={(e) => setForm((f) => ({ ...f, rechtsgebiet: e.target.value }))} data-testid={`akte-rg-select-${akte.id}`}
              className="w-full px-2 py-1 text-xs border border-gray-200 focus:border-[#b8893d] focus:outline-none bg-white">
              {RECHTSGEBIETE.map((r) => <option key={r}>{r}</option>)}
            </select>
            <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Angelegenheit / Kurzbeschreibung" data-testid={`akte-desc-input-${akte.id}`}
              className="w-full px-2 py-1 text-xs border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
          </div>
        ) : (
          <div>
            <div className="text-sm text-[#1B3A5C]" data-testid={`akte-rg-${akte.id}`}>{akte.rechtsgebiet}</div>
            {akte.description_excerpt && <div className="text-xs text-[#34536b] line-clamp-2">{akte.description_excerpt}</div>}
          </div>
        )}
      </div>
      <div className="md:col-span-2 text-xs">
        <span className={`inline-flex px-2 py-0.5 rounded-sm ${akte.archived ? 'bg-gray-200 text-gray-700' : 'bg-emerald-50 text-emerald-700'}`} data-testid={`akte-status-${akte.id}`}>
          {akte.archived ? 'Archiviert' : STATUS_LABEL[akte.status] || 'Neu'}
        </span>
      </div>
      <div className="md:col-span-3 flex flex-wrap justify-end gap-1.5">
        {edit ? (
          <>
            <button type="button" onClick={() => patch({ aktenzeichen: form.aktenzeichen, rechtsgebiet: form.rechtsgebiet, ...(form.description !== (akte.description_excerpt || '') ? { description: form.description } : {}) })} disabled={saving} data-testid={`akte-save-${akte.id}`}
              className={`${btn} bg-[#1B3A5C] text-white border-[#1B3A5C]`}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} SPEICHERN</button>
            <button type="button" onClick={() => setEdit(false)} className={`${btn} border-gray-200 text-[#34536b]`}><X className="w-3 h-3" /></button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setEdit(true)} data-testid={`akte-edit-${akte.id}`} title="Aktenzeichen / Angelegenheit bearbeiten" className={`${btn} border-gray-200 text-[#1B3A5C] hover:border-[#b8893d]`}><Pencil className="w-3 h-3" /> BEARBEITEN</button>
            <button type="button" onClick={() => patch({ archived: !akte.archived })} disabled={saving} data-testid={akte.archived ? `akte-reactivate-${akte.id}` : `akte-archive-${akte.id}`}
              className={`${btn} ${akte.archived ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}>
              {akte.archived ? <><ArchiveRestore className="w-3 h-3" /> REAKTIVIEREN</> : <><Archive className="w-3 h-3" /> ARCHIVIEREN</>}
            </button>
            <Link to={`/team-portal/${akte.id}`} data-testid={`akte-chat-${akte.id}`} className={`${btn} border-gray-200 text-[#1B3A5C] hover:border-[#1B3A5C]`}><MessageSquare className="w-3 h-3" /> CHAT</Link>
          </>
        )}
      </div>
    </div>
  );
};

const NeueAkteInline = ({ email, name, onCreated, onError }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ rechtsgebiet: 'Ausländerrecht', aktenzeichen: '', description: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    const [firstName, ...rest] = (name || '').trim().split(' ');
    try {
      const r = await api.post('/cases', { email, firstName: rest.length ? firstName : '', lastName: rest.join(' ') || firstName || email, ...form });
      onCreated(r.data); setOpen(false); setForm({ rechtsgebiet: 'Ausländerrecht', aktenzeichen: '', description: '' });
    } catch (e) { onError(e?.response?.data?.detail || 'Akte konnte nicht angelegt werden'); }
    finally { setSaving(false); }
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} data-testid="akte-neu-btn" className={`${btn} border-[#b8893d] text-[#b8893d] hover:bg-[#fbf7f0]`}><Plus className="w-3 h-3" /> NEUE AKTE</button>;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="akte-neu-form">
      <select value={form.rechtsgebiet} onChange={(e) => setForm((f) => ({ ...f, rechtsgebiet: e.target.value }))} data-testid="akte-neu-rg" className="px-2 py-1 text-xs border border-gray-200 bg-white">
        {RECHTSGEBIETE.map((r) => <option key={r}>{r}</option>)}
      </select>
      <input value={form.aktenzeichen} onChange={(e) => setForm((f) => ({ ...f, aktenzeichen: e.target.value }))} placeholder="AZ aus DATEV (000000-26, optional)" data-testid="akte-neu-az" className="px-2 py-1 text-xs font-mono border border-gray-200 w-40" />
      <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Angelegenheit" data-testid="akte-neu-desc" className="px-2 py-1 text-xs border border-gray-200 flex-1 min-w-[160px]" />
      <button type="button" onClick={submit} disabled={saving} data-testid="akte-neu-submit" className={`${btn} bg-[#b8893d] text-white border-[#b8893d]`}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} ANLEGEN</button>
      <button type="button" onClick={() => setOpen(false)} className={`${btn} border-gray-200 text-[#34536b]`}><X className="w-3 h-3" /></button>
    </div>
  );
};

export const MandantAktenPanel = ({ email, name, isAdmin = true }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [mnrEdit, setMnrEdit] = useState(false);
  const [mnr, setMnr] = useState('');
  const [verlaufTick, setVerlaufTick] = useState(0);

  const load = useCallback(async () => {
    try { const r = await api.get(`/cases/by-email?email=${encodeURIComponent(email)}`); setData(r.data); }
    catch (e) { setError(e?.response?.data?.detail || 'Akten konnten nicht geladen werden'); }
  }, [email]);
  useEffect(() => { load(); }, [load]);

  if (error && !data) return <div className="p-4 text-xs text-rose-700 flex items-center gap-2" data-testid="akten-panel-error"><AlertCircle className="w-4 h-4" /> {error}</div>;
  if (!data) return <div className="p-4 text-xs text-[#34536b] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lade Akten…</div>;

  const akten = data.items.filter((i) => !i.is_general);
  const general = data.items.find((i) => i.is_general);
  const mandant = data.mandant;
  const chatOffen = mandant ? mandant.chat_enabled !== false : true;

  const toggleChat = async () => {
    if (!mandant?.mandanten_nr) return;
    setBusy('chat');
    try { await api.patch(`/cases/mandanten/${mandant.mandanten_nr}`, { chat_enabled: !chatOffen }); await load(); }
    catch (e) { setError(e?.response?.data?.detail || 'Chat-Status konnte nicht geändert werden'); }
    finally { setBusy(''); }
  };
  const zugang = async () => {
    setBusy('zugang');
    try {
      const r = await api.post('/cases/zugang', { email, name });
      setError('');
      setData((d) => ({ ...d, zugang_result: r.data }));
      await load();
      setVerlaufTick((n) => n + 1);
    } catch (e) { setError(e?.response?.data?.detail || 'Zugang konnte nicht eingerichtet werden'); }
    finally { setBusy(''); }
  };
  const mnrVergeben = () => { setMnr(''); setError(''); setMnrEdit(true); };
  const mnrSpeichern = async () => {
    setBusy('mnr');
    try {
      if (mandant?.mandanten_nr) await api.patch(`/cases/mandanten/${mandant.mandanten_nr}`, { mandanten_nr: mnr.trim() });
      else await api.post('/cases/mandanten/nummer', { email, mandanten_nr: mnr.trim(), name });
      setMnrEdit(false); setError(''); await load();
    } catch (e) { setError(e?.response?.data?.detail || 'Mandantennummer konnte nicht gespeichert werden'); }
    finally { setBusy(''); }
  };
  const onSaved = (updated) => setData((d) => ({ ...d, items: d.items.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)) }));

  return (
    <div className="bg-[#fbfcfd] border-t border-gray-200" data-testid={`akten-panel-${email}`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="tracking-wider text-[#7a8a99]">M-NR</span>
          {mnrEdit ? (
            <>
              <input value={mnr} onChange={(e) => setMnr(e.target.value.replace(/\D/g, '').slice(0, 5))} pattern="4\d{4}" inputMode="numeric" placeholder="aus DATEV" title="Mandantennummer aus DATEV eintragen (4XXXX)" autoFocus data-testid="panel-mnr-input" className="w-24 px-2 py-1 font-mono text-xs border border-gray-200 focus:border-[#b8893d] focus:outline-none" />
              <button type="button" onClick={mnrSpeichern} disabled={busy === 'mnr'} data-testid="panel-mnr-save" className={`${btn} bg-[#1B3A5C] text-white border-[#1B3A5C]`}><Check className="w-3 h-3" /></button>
              <button type="button" onClick={() => setMnrEdit(false)} className={`${btn} border-gray-200 text-[#34536b]`}><X className="w-3 h-3" /></button>
            </>
          ) : (
            <>
              <span className="font-mono text-sm text-[#1B3A5C]" data-testid="panel-mnr">{mandant?.mandanten_nr || '—'}</span>
              {mandant?.mandanten_nr
                ? <button type="button" onClick={() => { setMnr(mandant.mandanten_nr); setMnrEdit(true); }} data-testid="panel-mnr-edit" className="text-[#b8893d] text-[10px] tracking-wider hover:underline">ÄNDERN</button>
                : <button type="button" onClick={mnrVergeben} data-testid="panel-mnr-assign" className={`${btn} bg-[#b8893d] text-white border-[#b8893d]`}>VERGEBEN</button>}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="tracking-wider text-[#7a8a99]">CHAT</span>
          <button type="button" onClick={toggleChat} disabled={!mandant?.mandanten_nr || busy === 'chat'} data-testid="panel-chat-toggle" aria-pressed={!chatOffen}
            title={mandant?.mandanten_nr ? '' : 'Erst Mandantennummer vergeben'}
            className={`${btn} ${chatOffen ? 'border-emerald-300 text-emerald-700 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700' : 'border-rose-300 text-rose-700 bg-rose-50 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700'}`}>
            {chatOffen ? <><Unlock className="w-3 h-3" /> OFFEN · SPERREN</> : <><Lock className="w-3 h-3" /> GESPERRT · REAKTIVIEREN</>}
          </button>
          {general && <Link to={`/team-portal/${general.id}`} data-testid="panel-general-chat" className={`${btn} border-gray-200 text-[#1B3A5C] hover:border-[#1B3A5C]`}><MessageSquare className="w-3 h-3" /> ALLGEMEINER CHAT</Link>}
        </div>
        <div className="flex items-center gap-2">
          <span className="tracking-wider text-[#7a8a99]">WEBSITE-ZUGANG</span>
          {data.konto ? (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm ${data.konto.passwort_gesetzt ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid="panel-konto-status">
              <KeyRound className="w-3 h-3" /> {data.konto.passwort_gesetzt ? (data.konto.auth_provider === 'google' ? 'Google-Login' : 'eingerichtet') : 'eingeladen – Passwort noch nicht gesetzt'}
            </span>
          ) : <span className="text-[#7a8a99]" data-testid="panel-konto-status">kein Konto</span>}
          {(!data.konto || !data.konto.passwort_gesetzt) && (
            <button type="button" onClick={zugang} disabled={busy === 'zugang'} data-testid="panel-zugang-btn" className={`${btn} border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white`}>
              {busy === 'zugang' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} {data.konto ? 'EINLADUNG ERNEUT SENDEN' : 'ZUGANG EINRICHTEN'}
            </button>
          )}
          {data.zugang_result && <span className="text-[10px] text-emerald-700" data-testid="panel-zugang-result">{data.zugang_result.status === 'vorhanden' ? 'Zugang besteht bereits' : data.zugang_result.mail === 'gesendet' ? 'Einladung gesendet' : 'Konto angelegt, Mail fehlgeschlagen'}</span>}
        </div>
        <AnsprechpersonSelect email={email} value={data.ansprechperson} onSaved={(ap) => setData((d) => ({ ...d, ansprechperson: ap }))} onError={setError} />
      </div>
      <ZugangVerlauf email={email} reload={verlaufTick} />
      {mandant?.mandanten_nr && <ZugaengeCodes mnr={mandant.mandanten_nr} email={email} name={mandant.name || name} onError={setError} />}
      {error && <div className="mx-4 mb-2 px-3 py-2 text-xs text-rose-700 bg-rose-50 border-l-2 border-rose-400 flex items-center justify-between" data-testid="akten-panel-error">{error}<button type="button" onClick={() => setError('')}><X className="w-3 h-3" /></button></div>}

      <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-[10px] tracking-wider text-[#34536b] font-semibold border-t border-gray-100 bg-[#f5f8fa]">
        <div className="col-span-3">AKTENZEICHEN</div><div className="col-span-4">ANGELEGENHEIT</div><div className="col-span-2">STATUS</div><div className="col-span-3 text-right">AKTIONEN</div>
      </div>
      {akten.length === 0 && <p className="px-4 py-4 text-xs text-[#7a8a99] border-t border-gray-100" data-testid="akten-leer">Noch keine Akten zu diesem Mandanten.</p>}
      {akten.map((a) => <AkteZeile key={a.id} akte={a} onSaved={onSaved} onError={setError} />)}
      <div className="px-4 py-3 border-t border-gray-100 flex justify-end">
        <NeueAkteInline email={email} name={name} onCreated={() => load()} onError={setError} />
      </div>
    </div>
  );
};

export default MandantAktenPanel;
